// @ts-nocheck
/* eslint-disable no-restricted-globals */
import { ChangeRequestListOfChangesModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import {
    AddedItemModel,
    ChangeRequestLineModel,
    ChangeRequestModel,
} from "@store/Library/Widgets/RequestOrderChange/RequestOrderChangePageContainer";

const enum probability {
    low = 1,
    medium = 2,
    high = 3,
}

const initialChangeValue: ChangeRequestListOfChangesModel = {
    probability: 9,
    typeOfChange: "N/A",
    lineId: "",
};

let listOfChanges: ChangeRequestListOfChangesModel[] = [initialChangeValue];

export const resetLikelihoodOfAcceptance = () => {
    listOfChanges = [initialChangeValue];
};

const likelihoodOfAcceptance = (changeRequest: ChangeRequestModel, originalOrder: OrderModel) => {
    resetLikelihoodOfAcceptance();

    if (changeRequest.newRequestedDate) {
        const sortedLeadTime = changeRequest.orderLines
            ?.map(x => parseInt(x.leadTimeInWeeks, 10))
            .sort((a, b) => a - b);

        const isThereLeadtime = sortedLeadTime.some(x => !isNaN(x));
        const farthestLeadTime = sortedLeadTime[sortedLeadTime.length - 1];
        const estLeadTimeDate = convertLeadTimeToDate(farthestLeadTime);

        if (estLeadTimeDate instanceof Date && !isNaN(estLeadTimeDate.valueOf())) {
            evaluateDates(originalOrder.requestedDeliveryDateDisplay!, estLeadTimeDate, changeRequest.newRequestedDate);
        } else if (isThereLeadtime) {
            const newTime = convertLeadTimeToDate(sortedLeadTime[0]);
            evaluateDates(originalOrder.requestedDeliveryDateDisplay!, newTime, changeRequest.newRequestedDate);
        }
    }

    if (!changeRequest.newRequestedDate) {
        removeChangeTypeFromList("Date");
    }

    if (changeRequest.newLines && changeRequest.newLines?.length > 0) {
        const plant = changeRequest.orderLines.find(x => x.plant)?.plant;

        changeRequest.newLines.forEach(line => {
            if (!line.product.properties.leadTimes) {
                return;
            }

            if (line.product.id) {
                removeChangeIdFromList(line.product.id);
            } else {
                removeChangeTypeFromList("New Item");
            }

            evaluateItems(line, plant!, originalOrder.requestedDeliveryDateDisplay!, changeRequest.newRequestedDate);
        });
    }

    if (changeRequest.newLines && changeRequest.newLines?.length === 0) {
        removeChangeTypeFromList("New Item");
    }

    if (changeRequest.orderLines?.some(x => Number(x.newQuantity) !== Number(x.originalQuantity))) {
        const linesWithNewQty = changeRequest.orderLines?.filter(
            x => Number(x.newQuantity) !== Number(x.originalQuantity),
        );

        linesWithNewQty.forEach(line => {
            if (!line.leadTimeInWeeks) {
                return;
            }

            if (line.orderLineId) {
                removeChangeIdFromList(line.orderLineId);
            } else {
                removeChangeTypeFromList("Quantity");
            }

            evaluteQuantity(line, originalOrder.requestedDeliveryDateDisplay!, changeRequest.newRequestedDate);
        });
    }

    if (changeRequest.orderLines?.some(x => Number(x.newQuantity) === Number(x.originalQuantity))) {
        const linesWithEqualQty = changeRequest.orderLines?.filter(
            x => Number(x.newQuantity) === Number(x.originalQuantity),
        );

        linesWithEqualQty.forEach(line => {
            if (line.orderLineId) {
                removeChangeIdFromList(line.orderLineId);
            }
        });
    }

    if (changeRequest.orderLines?.some(x => x.isCanceled)) {
        const linesCanceled = changeRequest.orderLines?.filter(x => x.isCanceled);

        linesCanceled.forEach(line => {
            if (!line.leadTimeInWeeks) {
                return;
            }

            if (line.orderLineId) {
                removeChangeIdFromList(line.orderLineId);
            } else {
                removeChangeTypeFromList("Canceled");
            }
            evaluteCanceledLine(line, originalOrder.requestedDeliveryDateDisplay!, changeRequest.newRequestedDate);
        });
    }

    listOfChanges.sort((a, b) => a.probability - b.probability);

    return listOfChanges.find(x => x !== undefined) || initialChangeValue;
};

const evaluateDates = (originalDate: Date, leadTimeDate: Date, newDate: Date) => {
    const plannedDate = new Date(newDate || originalDate);
    const _originalDate = new Date(originalDate);

    removeChangeTypeFromList("Date");

    if (newDate <= _originalDate && plannedDate < leadTimeDate) {
        listOfChanges.push({
            probability: probability["low"],
            typeOfChange: "Date",
        });
    }

    if (newDate >= _originalDate && newDate <= leadTimeDate && plannedDate <= leadTimeDate) {
        listOfChanges.push({ probability: probability["medium"], typeOfChange: "Date" });
    }

    if (newDate >= leadTimeDate && plannedDate < leadTimeDate) {
        listOfChanges.push({ probability: probability["high"], typeOfChange: "Date" });
    }

    if (newDate < leadTimeDate && plannedDate > leadTimeDate) {
        listOfChanges.push({
            probability: probability["low"],
            typeOfChange: "Date",
        });
    }

    if (newDate >= leadTimeDate && newDate <= _originalDate) {
        listOfChanges.push({ probability: probability["high"], typeOfChange: "Date" });
    }

    if (newDate >= _originalDate && newDate >= leadTimeDate) {
        listOfChanges.push({ probability: probability["high"], typeOfChange: "Date" });
    }
};

const evaluateItems = (newLine: AddedItemModel, plant: string, originalDate: Date, newDate?: Date) => {
    const plannedDate = new Date(newDate || originalDate);

    let leadTimes;
    if (newLine.product.properties.leadTimes) {
        leadTimes = JSON.parse(newLine.product.properties.leadTimes) as any[];
    }

    const currentLineLeadTime = leadTimes?.find(x => x.Plant === plant);
    const estLeadTimeDate = convertLeadTimeToDate(parseInt(currentLineLeadTime?.LeadTime, 10));
    const outsideOfDateFence = plannedDate < estLeadTimeDate;

    if (outsideOfDateFence) {
        listOfChanges.push({ probability: probability["low"], typeOfChange: "New Item", lineId: newLine.product.id });
    }

    if (!outsideOfDateFence) {
        listOfChanges.push({ probability: probability["high"], typeOfChange: "New Item", lineId: newLine.product.id });
    }
};

const evaluteQuantity = (line: ChangeRequestLineModel, originalDate: Date, newDate?: Date) => {
    const plannedDate = new Date(newDate || originalDate);
    const estLeadTimeDate = convertLeadTimeToDate(parseInt(line.leadTimeInWeeks, 10));
    const outsideOfDateFence = plannedDate < estLeadTimeDate;

    if (outsideOfDateFence) {
        listOfChanges.push({ probability: probability["low"], typeOfChange: "Quantity", lineId: line.orderLineId });
    }

    if (!outsideOfDateFence) {
        listOfChanges.push({ probability: probability["high"], typeOfChange: "Quantity", lineId: line.orderLineId });
    }
};

const evaluteCanceledLine = (line: ChangeRequestLineModel, originalDate: Date, newDate?: Date) => {
    const plannedDate = new Date(newDate || originalDate);
    const estLeadTimeDate = convertLeadTimeToDate(parseInt(line.leadTimeInWeeks, 10));
    const outsideOfDateFence = plannedDate < estLeadTimeDate;

    if (outsideOfDateFence) {
        listOfChanges.push({ probability: probability["low"], typeOfChange: "Canceled", lineId: line.orderLineId });
    }

    if (!outsideOfDateFence) {
        listOfChanges.push({ probability: probability["high"], typeOfChange: "Canceled", lineId: line.orderLineId });
    }
};

const removeChangeIdFromList = (id: string) => {
    if (listOfChanges.some(x => x.lineId === id)) {
        const indexOfLine = listOfChanges.findIndex(x => x.lineId === id);
        listOfChanges.splice(indexOfLine, 1);
    }
};

const removeChangeTypeFromList = (type: string) => {
    if (listOfChanges.some(x => x.typeOfChange === type)) {
        const indexOfLine = listOfChanges.findIndex(x => x.typeOfChange === type);
        indexOfLine > -1 ? listOfChanges.splice(indexOfLine, 1) : null;
        removeChangeTypeFromList(type);
    }
};

export const convertLeadTimeToDate = (leadTimeInWeeks: string | number) => {
    const leadTimeInDays =
        typeof leadTimeInWeeks === "string" ? parseInt(leadTimeInWeeks, 10) * 7 : leadTimeInWeeks * 7;
    const leadTimeDateTime = new Date().setDate(new Date().getDate() + leadTimeInDays);
    return new Date(leadTimeDateTime);
};

export default likelihoodOfAcceptance;
